import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { InView } from 'react-intersection-observer';
import isEmpty from 'lodash/isEmpty';

import { fetchNewReleases } from 'Redux/ducks/algolia-calls';
import Backtotopbutton from 'Components/Utility/Mobile/Backtotopbutton';
import FanFavouritesSection from 'Components/FanFavourites/FanFavouritesSection';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import HitCard from 'Components/Cards/HitCard';
import Meta from 'Components/Utility/SEO/Meta';
import NotFound from 'Components/NotFound/NotFound';
import Paginate from 'Components/Utility/Paginate';
import PriceContainer from 'Components/Utility/Price/PriceContainer';
import SkeletonHitCard from 'Components/Cards/SkeletonHitCard';
import TakeOver from 'Components/Banner/TakeOver';

import 'Features/StaticCategory/components/StaticSearch.scss';

const messages = defineMessages({
  title: {
    id: 'newreleases.title',
    defaultMessage: 'New PC Game Releases',
  },
  fullTitle: {
    id: 'newreleases.seotitle',
    defaultMessage: 'New PC Games | Latest Steam and PC Releases',
  },
  desc: {
    id: 'newreleases.seodesc',
    defaultMessage: 'If you\'re looking for the latest greatest games for PC, Mac and Linux, we are Fanatical about always bringing you the best deals on the planet from top publishers like Bethesda, Warner Bros, 2K, SEGA and more!',
  },
  page: {
    id: 'newreleases.page',
    defaultMessage: 'Page',
  },
});

const NewReleases = ({
  location: { search },
  intl: { formatMessage },
}) => {
  const [hitsObject, setHits] = useState({ hits: null, nbHits: null, hitsPerPage: 36 });
  const [fetchError, setFetchError] = useState(null);
  const page = Number(getUrlParameter('page', search) || 1);

  useEffect(() => {
    const fetchHits = async () => {
      const hits = await fetchNewReleases(36, page - 1);
      setHits(hits);
    };

    fetchHits().catch(setFetchError);
  }, [page]);

  if (fetchError) {
    throw fetchError;
  }

  const { hits, nbHits, hitsPerPage } = hitsObject;

  const seoTitle = `${formatMessage(messages.fullTitle)}${page > 1 ? ` | ${formatMessage(messages.page)} ${page}` : ''} | Fanatical`;
  const h1Title = `${formatMessage(messages.title)}${page > 1 ? ` - ${formatMessage(messages.page)} ${page}` : ''}`;
  const desc = formatMessage(messages.desc);

  return (
    <div>
      <main className="NewReleases">
        <Meta
          title={seoTitle}
          description={desc}
          url="new-releases"
          hits={hits}
          nbHits={nbHits}
          hitsPerPage={hitsPerPage}
          pageType="CollectionPage"
          pageName={h1Title}
        />
        {hits && !hits.length &&
          <NotFound />
        }
        {((hits && hits.length) || hits === null) &&
          <React.Fragment>
            <TakeOver appearsOn="new_releases" />
            <div className="container">
              <InView>
                {({ inView, ref }) => (
                  <div ref={ref}>
                    <h1 className="pt-5 mb-3 h1">{h1Title}</h1>
                    <p className="seo-blurb-text mb-4">
                      <FormattedMessage
                        id="new-releases.best-selling.intro"
                        defaultMessage="If you’re looking for the latest new game releases you’ve come to the right place! Here you’ll find all the newest chart-topping games, including our best sellers. See which games are most wanted by gamers just like you, or check out game review scores on Metacritic."
                      />
                    </p>
                    {!inView &&
                      <div className="d-block d-sm-none">
                        <Backtotopbutton />
                      </div>
                    }
                  </div>
                )}
              </InView>

              <React.Fragment>
                {hits === null &&
                  <div className="row">
                    <SkeletonHitCard
                      numberOfCards={6}
                      lgSize={4}
                    />
                  </div>
                }

                {!isEmpty(hits) &&
                  <ul className="HitCardsRow row">
                    {hits && hits.slice(0, 6).map(hit =>
                      <HitCard
                        key={hit.objectID}
                        hit={hit}
                        lgSize={4}
                        labelOverride="newrelease"
                        hitCardContainerTag="li"
                        hitCardNameTag="h3"
                      >
                        <PriceContainer
                          discountPercent={hit.discount_percent}
                          price={hit.price}
                          tiered={hit.tiered}
                          slug={hit.slug}
                          hideDiscount={hit.hide_discount}
                          payWhatYouWant={hit.pay_what_you_want}
                        />
                      </HitCard>,
                    )}
                  </ul>
                }
              </React.Fragment>


              <React.Fragment>
                {hits === null &&
                  <div className="row">
                    <SkeletonHitCard
                      numberOfCards={72}
                      lgSize={3}
                    />
                  </div>
                }

                {!isEmpty(hits) &&
                  <ul className="HitCardsRow row">
                    {hits.slice(6, 36).map(hit =>
                      <HitCard
                        lazy
                        key={hit.objectID}
                        hit={hit}
                        lgSize={4}
                        labelOverride="newrelease"
                        hitCardContainerTag="li"
                        hitCardNameTag="h3"
                      >
                        <PriceContainer
                          discountPercent={hit.discount_percent}
                          price={hit.price}
                          tiered={hit.tiered}
                          slug={hit.slug}
                          hideDiscount={hit.hide_discount}
                          payWhatYouWant={hit.pay_what_you_want}
                        />
                      </HitCard>,
                    )}
                  </ul>
                }
              </React.Fragment>


              <div className="row">
                <Paginate hits={hitsObject} />
              </div>
              <FanFavouritesSection newReleases />
            </div>
          </React.Fragment>
        }
      </main>
    </div>
  );
};

NewReleases.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};

const mapStateToProps = ({
  router: {
    location,
  },
}) => ({ location });

export default connect(mapStateToProps)(injectIntl(NewReleases));
